import React, { useRef, useEffect, useState, useCallback } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { useTheme } from '@mui/material/styles';
import { icons } from "./icons";
import API_BASE_URL from '../config';

const ScenariosMapViewInitial = () => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const theme = useTheme();

  const addIconToMap = useCallback((iconUrl, iconId) => {
    if (!map.current) return Promise.reject("Map not initialized");
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = iconUrl;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        context.drawImage(img, 0, 0);
        const pngUrl = canvas.toDataURL('image/png');

        map.current.loadImage(pngUrl, (error, image) => {
          if (error) return reject(error);
          if (map.current.hasImage(iconId)) map.current.removeImage(iconId);
          map.current.addImage(iconId, image);
          resolve();
        });
      };
      img.onerror = reject;
    });
  }, []);

  // Map initialization effect
  useEffect(() => {
    mapboxgl.accessToken = 'pk.eyJ1IjoibXZwYXNzb3MiLCJhIjoiY2t6em4xcnpwMGJ2bDNicDYyemM0bWU4YyJ9.fR55BgEafulrz4yLfY2oRA';
    if (map.current) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: theme.palette.mode === 'dark' ? 'mapbox://styles/mapbox/dark-v10' : 'mapbox://styles/mapbox/light-v10',
      center: [18.0656, 59.3327],
      zoom: 12,
    });

    map.current.on('load', () => setIsMapLoaded(true));

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [theme.palette.mode]);

  // Effect to add sources and layers once the map is loaded
  useEffect(() => {
    if (!isMapLoaded) return;

    const addLayers = async () => {
      // Load all icons first
      const iconPromises = Object.entries(icons).map(([name, url]) => addIconToMap(url, name));
      await Promise.all(iconPromises).catch(err => console.error("Failed to load one or more icons:", err));

      if (!map.current) return;

      // Clean up old layers and sources before adding new ones
      ['combined-network-nodes', 'combined-network-lines'].forEach(id => {
        if (map.current.getLayer(id)) map.current.removeLayer(id);
      });
      if (map.current.getSource('network-tiles')) {
        map.current.removeSource('network-tiles');
      }

      // --- UPDATED TO USE DJANGO MBTILES ENDPOINT ---
      map.current.addSource('network-tiles', {
        type: 'vector',
        tiles: [`${API_BASE_URL}/api/tiles/combined_network/{z}/{x}/{y}.pbf`],
        minzoom: 0,
        maxzoom: 13
      });

      const SOURCE_LAYER_NAME = 'network_with_critical_flags'; // Use the correct source layer name
      const getIconName = (baseName) => theme.palette.mode === 'dark' ? `${baseName}-dark` : baseName;

      // Add the lines layer
      map.current.addLayer({
        id: 'combined-network-lines',
        type: 'line',
        source: 'network-tiles',
        'source-layer': SOURCE_LAYER_NAME,
        filter: ['all', ['==', '$type', 'LineString'], ['!=', 'line_type', 'Transmission']],
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': theme.palette.mode === 'dark' ? '#555' : '#D3D3D3', 'line-width': 1 },
      });

      // Add the nodes layer
      map.current.addLayer({
        id: 'combined-network-nodes',
        type: 'symbol',
        source: 'network-tiles',
        'source-layer': SOURCE_LAYER_NAME,
        filter: ['all', ['==', '$type', 'Point'], ['!=', 'name', 'Road']],
        layout: {
          'icon-image': [
            'match', ['get', 'name'],
            'School', getIconName('school'), 'Port', getIconName('directions-boat'), 'Harbour', getIconName('directions-boat'),
            'Household', getIconName('home'), 'Supermarket', getIconName('local-grocery-store'), 'Warehouse', getIconName('warehouse'),
            'Farm', getIconName('agriculture'), 'Power station', getIconName('factory'), 'Power substation', getIconName('power'),
            'Hospital', getIconName('local-hospital'), 'Clinic', getIconName('local-hospital'), 'Pharmacy', getIconName('pharmacy'),
            'Fire station', getIconName('fire-station'), 'Police station', getIconName('police'), 'Military base', getIconName('police'),
            'Train station', getIconName('train'), 'Airport', getIconName('plane'), 'Government office', getIconName('office'),
            'Courthouse', getIconName('office'), 'City hall', getIconName('office'), 'Communication tower', getIconName('communication-tower'),
            'Water reservoir', getIconName('reservoir'), 'Water plant', getIconName('droplet'), 'Wastewater plant', getIconName('droplet'),
            getIconName('home') // Default fallback
          ],
          'icon-size': 1,
          'icon-allow-overlap': true,
        },
        minzoom: 12,
      });
    };

    addLayers();

  }, [isMapLoaded, theme.palette.mode, addIconToMap]);

  return (
    <div
      ref={mapContainer}
      style={{ width: '100%', height: 'calc(100vh - 200px)', minHeight: '540px', borderRadius: '8px' }}
    />
  );
};

export default ScenariosMapViewInitial;